// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Admin panel  (/admin/bulk-orders/:ref — one B2B / party-feast order, the staff work screen)
// REAL, from GET /api/admin/bulk-orders/{ref}: account & contact, GSTIN/PO reference, how it came in, the line items staff priced, the delivery
// address, payment terms/status (staff-recorded — never a real payment or credit balance), the status timeline with the next step highlighted, a
// captain handed off from the real fleet directory (utils/fleetApi — the same picker fleet dispatch uses), and a PRIVATE ops note. Editors/admins
// move the order forward or cancel it, mark it paid/unpaid, and assign/take back a captain once it's confirmed. Viewers see everything read-only.
// Coming-soon sample panels (SoonPanel, no invented figures): GST e-invoicing/IRN portal sync, an automated corporate credit-line ledger, reefer
// cold-chain telemetry, digital scale certification for the consignment weight.
// Neighbours: pages/admin/AdminBulkOrders, utils/bulkOrders, utils/fleetApi, components/admin/AdminParts.
// ─────────────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, CircleAlert, CircleCheck, Loader2, MessageCircle, Phone, X } from "lucide-react";
import { SoonPanel, panel } from "../../components/admin/AdminParts";
import useAuth from "../../hooks/useAuth";
import { ApiError } from "../../utils/api";
import { formatWhen } from "../../utils/orders";
import { canWriteFleet, fetchDrivers, type DriverRow } from "../../utils/fleetApi";
import {
  BULK_STATUS_PILL,
  CHANNEL_LABEL,
  STATUS_FLOW,
  STATUS_LABEL,
  canWriteBulkOrders,
  fetchBulkOrder,
  inr,
  nextStatus,
  saveBulkOrderNote,
  setBulkOrderDriver,
  setBulkOrderPayment,
  setBulkOrderStatus,
  type BulkOrder,
} from "../../utils/bulkOrders";

const digits = (phone: string) => phone.replace(/\D/g, "");

export default function AdminBulkOrderDetail() {
  const { ref = "" } = useParams();
  const auth = useAuth();
  const canWrite = canWriteBulkOrders(auth.user?.role);
  const [order, setOrder] = useState<BulkOrder | null>(null);
  const [failed, setFailed] = useState<"missing" | "error" | null>(null);
  const [drivers, setDrivers] = useState<DriverRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    try {
      const loaded = await fetchBulkOrder(ref);
      setOrder(loaded);
      setNote(loaded.opsNote ?? "");
      setFailed(null);
    } catch (caught) {
      setFailed(caught instanceof ApiError && caught.status === 404 ? "missing" : "error");
    }
  }, [ref]);

  useEffect(() => { setOrder(null); void load(); }, [load]);
  useEffect(() => { fetchDrivers().then((d) => setDrivers(d.drivers), () => undefined); }, []);

  if (failed) {
    return (
      <div className="flex flex-col items-start gap-3">
        <p role="alert" className="rounded-xl bg-coral-500/10 p-4 font-semibold">{failed === "missing" ? "That bulk order doesn't exist." : "We couldn't load this order. Reload to try again."}</p>
        <Link to="/admin/bulk-orders" className="inline-flex min-h-11 items-center gap-1 font-bold underline"><ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back to bulk orders</Link>
      </div>
    );
  }
  if (!order) return <p role="status" className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Loading…</p>;

  const next = nextStatus(order.status);
  const step = STATUS_FLOW.indexOf(order.status);
  const canAssign = order.status === "confirmed" || order.status === "preparing" || order.status === "dispatched";

  const move = async (target: typeof order.status) => {
    setBusy(true); setError(null); setNotice(null);
    try {
      await setBulkOrderStatus(order.ref, target);
      setCancelling(false);
      await load();
      setNotice(target === "cancelled" ? "The bulk order was cancelled." : `Moved to “${STATUS_LABEL[target]}”.`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "We couldn't change that order.");
    } finally {
      setBusy(false);
    }
  };

  const assign = async (driverId: string | null) => {
    setBusy(true); setError(null); setNotice(null);
    try {
      await setBulkOrderDriver(order.ref, driverId);
      await load();
      setNotice(driverId ? "Handed to the captain." : "Taken back — no captain assigned.");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "We couldn't change the captain.");
    } finally {
      setBusy(false);
    }
  };

  const togglePayment = async () => {
    setBusy(true); setError(null); setNotice(null);
    try {
      await setBulkOrderPayment(order.ref, order.paymentStatus === "paid" ? "unpaid" : "paid");
      await load();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "We couldn't update the payment status.");
    } finally {
      setBusy(false);
    }
  };

  const saveNote = async () => {
    setBusy(true); setError(null); setNotice(null);
    try {
      const saved = await saveBulkOrderNote(order.ref, note);
      setOrder(saved);
      setNotice(note.trim() ? "Note saved." : "Note cleared.");
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "We couldn't save the note.");
    } finally {
      setBusy(false);
    }
  };

  const actions = canWrite && order.active && (
    <>
      <button type="button" onClick={() => setCancelling(true)} disabled={busy} className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-coral-500/10 px-4 text-sm font-bold text-coral-600 disabled:opacity-60 dark:text-coral-300"><X className="h-4 w-4" aria-hidden="true" /> Cancel</button>
      {next && <button type="button" onClick={() => void move(next)} disabled={busy} className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-teal-700 px-5 text-sm font-bold text-white shadow-md disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Check className="h-4 w-4" aria-hidden="true" />} Move to {STATUS_LABEL[next]}</button>}
    </>
  );

  return (
    <div className="flex flex-col gap-5 pb-28">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-2 text-sm">
          <Link to="/admin/bulk-orders" className="inline-flex min-h-11 items-center gap-1 rounded-lg bg-ocean-700/10 px-3 font-bold dark:bg-white/10"><ArrowLeft className="h-4 w-4" aria-hidden="true" /> Back</Link>
          <span aria-hidden="true">/</span><span className="text-xs font-bold uppercase tracking-wider">B2B &amp; bulk orders</span><span aria-hidden="true">/</span><span className="font-bold">{order.ref}</span>
        </nav>
        <p className="text-xs">Logged {formatWhen(order.createdAt)} by {order.createdBy.replace("@staff.zenfresh.invalid", "")}</p>
      </div>

      <header className="flex flex-col justify-between gap-3 lg:flex-row lg:items-center">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-display text-3xl font-bold">{order.accountName}</h1>
            <span className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wider ${BULK_STATUS_PILL[order.status]}`} data-testid="bulk-status">{STATUS_LABEL[order.status]}</span>
            <span className="rounded-full bg-ocean-700/10 px-3 py-1 text-xs font-bold dark:bg-white/10">{order.paymentStatus === "paid" ? "Paid" : "Unpaid"}</span>
          </div>
          <p className="mt-1 text-sm text-ocean-800/85 dark:text-foam-100/80">{CHANNEL_LABEL[order.channel]} · {order.items.length} line {order.items.length === 1 ? "item" : "items"} · {order.delivery.city}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">{actions}</div>
      </header>

      <p role="status" className="min-h-5 text-sm font-semibold text-teal-700 dark:text-teal-300">{notice}</p>
      <p role="alert" className="flex min-h-5 items-center gap-1.5 text-sm text-coral-600 dark:text-coral-300">{error && <CircleAlert className="h-4 w-4" aria-hidden="true" />}{error}</p>
      {cancelling && (
        <div role="alertdialog" aria-label={`Cancel bulk order ${order.ref}?`} className="rounded-2xl bg-coral-500/10 p-4">
          <p className="font-bold">Cancel bulk order {order.ref}?</p>
          <p className="mt-1 text-sm">This is a staff record — nothing is refunded automatically; settle any advance with the account directly.</p>
          <div className="mt-3 flex gap-2">
            <button type="button" disabled={busy} onClick={() => void move("cancelled")} className="min-h-11 rounded-lg bg-coral-600 px-5 text-sm font-bold text-white disabled:opacity-60">Yes, cancel it</button>
            <button type="button" onClick={() => setCancelling(false)} className="min-h-11 rounded-lg bg-ocean-700/10 px-4 text-sm font-bold dark:bg-white/10">Keep it</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
        <div className="flex flex-col gap-5 xl:col-span-8">
          <section aria-labelledby="acct-h" className={panel}>
            <h2 id="acct-h" className="sr-only">Account</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-xl bg-ocean-700/5 p-4 dark:bg-white/5">
                <p className="text-[11px] font-bold uppercase tracking-wider">Contact</p>
                <p className="mt-2 font-bold">{order.contactName ?? order.accountName}</p>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <p className="text-sm">{order.phone}{order.email ? ` · ${order.email}` : ""}</p>
                  <span className="flex gap-1.5">
                    <a href={`tel:${order.phone}`} aria-label={`Call ${order.accountName}`} className="flex h-11 w-11 items-center justify-center rounded-lg bg-white text-ocean-900 shadow-sm dark:bg-ocean-900 dark:text-foam-50"><Phone className="h-4 w-4" aria-hidden="true" /></a>
                    <a href={`https://wa.me/${digits(order.phone)}`} target="_blank" rel="noopener noreferrer" aria-label={`WhatsApp ${order.accountName} (opens in a new tab)`} className="flex h-11 w-11 items-center justify-center rounded-lg bg-whatsapp text-ocean-950 shadow-sm"><MessageCircle className="h-4 w-4" aria-hidden="true" /></a>
                  </span>
                </div>
                <p className="mt-2 text-xs">GSTIN: <span className="font-mono">{order.gstin ?? "not given"}</span> · PO ref: {order.poReference ?? "none"}</p>
              </div>
              <div className="rounded-xl bg-ocean-700/5 p-4 dark:bg-white/5">
                <p className="text-[11px] font-bold uppercase tracking-wider">Delivery</p>
                <p className="mt-2 text-sm font-semibold">{order.delivery.addressLine}, {order.delivery.city} - {order.delivery.pincode}</p>
                {order.delivery.notes && <p className="mt-2 rounded-lg bg-ocean-700/10 p-2 text-xs dark:bg-white/10"><strong>Notes:</strong> {order.delivery.notes}</p>}
                {order.requestedFor && <p className="mt-2 text-xs">Requested for: {formatWhen(order.requestedFor)}</p>}
              </div>
            </div>
          </section>

          <section aria-labelledby="lines-h" className={panel}>
            <div className="flex flex-wrap items-center gap-2"><h2 id="lines-h" className="font-display text-lg font-bold">Line items</h2><span className="rounded-full bg-ocean-700/10 px-2.5 py-0.5 text-xs font-bold dark:bg-white/10">{order.totalWeightKg > 0 ? `${order.totalWeightKg} kg` : "weight not entered"}</span></div>
            <ul className="mt-3 flex flex-col gap-2">
              {order.items.map((line, index) => (
                <li key={index} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-ocean-700/5 p-3 dark:bg-white/5">
                  <div><p className="font-bold">{line.description}</p><p className="text-xs text-ocean-800/80 dark:text-foam-100/75">{line.weightKg ? `${line.weightKg} kg` : "no weight entered"}{line.catchId ? ` · catalog ref ${line.catchId}` : ""}</p></div>
                  <p className="font-display font-bold">{inr(line.amountInr)}</p>
                </li>
              ))}
            </ul>
            <div className="mt-4 rounded-xl bg-ocean-700/5 p-4 dark:bg-white/5">
              <div className="flex justify-between text-sm"><span>Subtotal</span><span>{inr(order.subtotal)}</span></div>
              <div className="flex justify-between text-sm"><span>Delivery</span><span>{order.deliveryFee === 0 ? "Free" : inr(order.deliveryFee)}</span></div>
              <div className="flex justify-between text-sm font-bold"><span>Total</span><span>{inr(order.total)}</span></div>
              <p className="mt-2 text-xs">Payment terms: {order.paymentTerms || "not recorded"}</p>
              {canWrite && (
                <button type="button" onClick={() => void togglePayment()} disabled={busy} className="mt-2 min-h-11 rounded-lg bg-ocean-700/10 px-4 text-sm font-bold disabled:opacity-60 dark:bg-white/10">Mark as {order.paymentStatus === "paid" ? "unpaid" : "paid"}</button>
              )}
            </div>
          </section>

          <SoonPanel title="GST e-invoicing &amp; IRN sync" subtitle="Automatic invoice registration with the GST portal — no e-invoicing integration exists; the GSTIN above is only checksum-validated.">
            <p className="text-sm">IRN — · Ack no. — · QR code —</p>
          </SoonPanel>
        </div>

        <div className="flex flex-col gap-5 xl:col-span-4">
          <section aria-labelledby="life-h" className={panel}>
            <div className="flex items-center justify-between"><h2 id="life-h" className="font-display text-lg font-bold">Order lifecycle</h2><span className="text-xs font-semibold">{order.status === "cancelled" ? "Cancelled" : `Step ${Math.max(1, step + 1)} of ${STATUS_FLOW.length}`}</span></div>
            <ol className="mt-3 flex flex-col gap-3 border-l-2 border-ocean-700/15 pl-5" data-testid="bulk-timeline">
              {order.history.map((h) => (
                <li key={`${h.status}-${h.at}`} className="relative">
                  <span aria-hidden="true" className="absolute -left-[1.85rem] top-1 flex h-4 w-4 items-center justify-center rounded-full bg-teal-700 text-white"><CircleCheck className="h-3 w-3" /></span>
                  <p className="text-[11px] font-bold uppercase text-teal-700 dark:text-teal-300">{formatWhen(h.at)} · done</p>
                  <p className="text-sm font-bold">{STATUS_LABEL[h.status]}</p>
                </li>
              ))}
              {order.active && next && (
                <li className="relative rounded-lg bg-coral-500/10 p-2">
                  <span aria-hidden="true" className="absolute -left-[1.85rem] top-3 h-4 w-4 animate-pulse rounded-full bg-coral-600 motion-reduce:animate-none" />
                  <p className="text-[11px] font-bold uppercase text-coral-600 dark:text-coral-300">Next</p>
                  <p className="text-sm font-bold">{STATUS_LABEL[next]}</p>
                </li>
              )}
              {order.active && STATUS_FLOW.slice(step + 2).map((s) => (
                <li key={s} className="relative opacity-60"><span aria-hidden="true" className="absolute -left-[1.85rem] top-1 h-4 w-4 rounded-full bg-ocean-700/25" /><p className="text-sm font-bold">{STATUS_LABEL[s]}</p></li>
              ))}
            </ol>
          </section>

          <section aria-label="Assigned captain" className={panel}>
            <h2 className="font-display text-lg font-bold">Assigned captain</h2>
            {order.driverId ? (
              <p className="mt-2 text-sm"><Link to={`/admin/drivers/${order.driverId}`} className="font-bold underline">{order.driverName}</Link>{order.driverAssignedAt ? <span className="text-xs"> · handed over {formatWhen(order.driverAssignedAt)}</span> : null}{canWrite && <button type="button" onClick={() => void assign(null)} disabled={busy} className="ml-2 text-xs font-bold underline disabled:opacity-60">Take back</button>}</p>
            ) : canWrite && canWriteFleet(auth.user?.role) && canAssign ? (
              <label className="mt-2 block text-sm">Hand to a captain
                <select onChange={(e) => { if (e.target.value) void assign(e.target.value); }} defaultValue="" disabled={busy} className="mt-1 min-h-11 w-full rounded-lg bg-ocean-700/5 px-3 dark:bg-white/10">
                  <option value="" disabled>Choose an available captain…</option>
                  {drivers.filter((d) => d.driver.available).map((d) => <option key={d.driver.id} value={d.driver.id}>{d.driver.name}</option>)}
                </select>
              </label>
            ) : (
              <p className="mt-2 text-sm">{canAssign ? "No captain yet." : "Confirm the order before assigning a captain."}</p>
            )}
          </section>

          <section aria-labelledby="note-h" className={panel}>
            <div className="flex items-center justify-between"><h2 id="note-h" className="font-display text-lg font-bold">Ops notes</h2><span className="text-xs">Staff only</span></div>
            <label htmlFor="bulk-note" className="mt-2 block text-xs font-semibold">Handling note</label>
            <textarea id="bulk-note" value={note} maxLength={500} readOnly={!canWrite} onChange={(e) => setNote(e.target.value)} rows={3} className="mt-1 w-full rounded-lg bg-ocean-700/5 p-3 text-sm focus-visible:outline-2 focus-visible:outline-teal-600 dark:bg-white/5" />
            <div className="mt-2 flex items-center justify-between gap-2">
              {canWrite && <button type="button" onClick={() => void saveNote()} disabled={busy || note.trim() === (order.opsNote ?? "")} className="min-h-11 rounded-lg bg-ocean-800 px-5 text-sm font-bold text-white disabled:opacity-50">Save note</button>}
              <span className="text-xs italic">{order.opsNote && order.opsNoteBy && order.opsNoteAt ? `${order.opsNoteBy.replace("@staff.zenfresh.invalid", "")} · ${formatWhen(order.opsNoteAt)}` : "No note yet"}</span>
            </div>
          </section>

          <SoonPanel title="Reefer cold-chain telemetry" subtitle="Sample — needs a temperature probe on the delivery van">
            <p className="font-display text-3xl font-bold">— °C</p>
          </SoonPanel>
          <SoonPanel title="Digital scale certification" subtitle="A certified weighbridge/scale reading against the quoted weight — no such device is integrated">
            <p className="text-sm">Certified weight — · Variance —</p>
          </SoonPanel>
        </div>
      </div>
    </div>
  );
}
